import { useState, useEffect } from 'react';
import { Folder, GitBranch, Clock, Play, Settings } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  description?: string;
  build_log?: string;
  status: string;
  lastModified?: string;
  updated_at?: string;
  stack?: string[];
  project_type?: string;
  url?: string;
}

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await fetch('/projects/manifest.json');
      if (res.ok) {
        const data = await res.json();
        setProjects(Object.values(data.projects || {}));
      }
    } catch {
      // Demo data
      setProjects([
        {
          id: 'synapse',
          name: 'Synapse Pipeline',
          description: 'AI task orchestration engine',
          status: 'active',
          lastModified: '2025-12-07',
          stack: ['Rust', 'Axum', 'WebSocket']
        },
        {
          id: 'controldeck',
          name: 'ControlDeck',
          description: 'Dashboard UI for Mothership',
          status: 'active',
          lastModified: '2025-12-07',
          stack: ['React', 'TypeScript', 'Tailwind']
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'deployed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'building': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Folder className="w-6 h-6 text-violet-400" />
            Projects
          </h1>
          <p className="text-gray-400">Internal development projects</p>
        </div>
        <button className="px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 rounded-lg flex items-center gap-2 transition">
          <Folder className="w-4 h-4" />
          New Project
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400">Loading projects...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-[#161b22]/30 backdrop-blur-xl backdrop-saturate-150 border border-[#30363d] rounded-lg p-5 hover:border-violet-500/30 transition group"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-violet-400 transition">
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-400">{project.description || project.build_log || 'No description'}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs border ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {project.stack ? (
                  project.stack.map((tech) => (
                    <span key={tech} className="px-2 py-0.5 bg-white/5 rounded text-xs text-gray-300">
                      {tech}
                    </span>
                  ))
                ) : project.project_type ? (
                  <span className="px-2 py-0.5 bg-violet-500/20 rounded text-xs text-violet-300">
                    {project.project_type}
                  </span>
                ) : null}
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {project.lastModified || (project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'Unknown')}
                </span>
                <div className="flex gap-2">
                  {project.url && (
                    <a href={project.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-white/5 rounded transition" title="Open">
                      <Play className="w-4 h-4 text-green-400" />
                    </a>
                  )}
                  <button className="p-2 hover:bg-white/5 rounded transition" title="Git">
                    <GitBranch className="w-4 h-4 text-cyan-400" />
                  </button>
                  <button className="p-2 hover:bg-white/5 rounded transition" title="Settings">
                    <Settings className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {projects.length === 0 && (
            <div className="col-span-2 text-center text-gray-500 py-12">
              No projects found
            </div>
          )}
        </div>
      )}
    </main>
  );
}
